import type { CharacterAnimationAlias } from './types';

export type CharacterAnimationDebugMode = 'off' | 'standard' | 'verbose';

export interface CharacterAnimationActionDebugState {
  readonly alias: string | null;
  readonly clipName: string | null;
  readonly time: number;
  readonly duration: number;
  readonly running: boolean;
  readonly loop: number;
  readonly weight: number;
  readonly enabled: boolean;
  readonly clampWhenFinished: boolean;
}

export interface CharacterAnimationDebugState {
  readonly requestedAlias: string | null;
  readonly resolvedAlias: string | null;
  readonly resolvedClipName: string | null;
  readonly success: boolean;
  readonly activeBefore: CharacterAnimationActionDebugState | null;
  readonly activeAfter: CharacterAnimationActionDebugState | null;
  readonly lastFailureReason: string | null;
  readonly suspiciousReasons: readonly string[];
}

export interface MinigameCharacterAnimationDebugPayload {
  readonly minigameId: string;
  readonly playerId: string;
  readonly isLocalPlayer?: boolean;
  readonly status: string;
  readonly speed: number;
  readonly facingYaw: number;
  readonly deltaSeconds: number;
  readonly requestedAlias: CharacterAnimationAlias;
  readonly selectedAlias: CharacterAnimationAlias;
  readonly fallbackAlias: CharacterAnimationAlias;
  readonly playedRequestedAlias: boolean;
  readonly playedFallbackAlias: boolean;
  readonly playedIdleFallback: boolean;
  readonly failureReason: string | null;
  readonly fallbackReason: string | null;
  readonly rigState: CharacterAnimationDebugState | null;
}

export interface CharacterAnimationLogEvent {
  readonly source: 'rig' | 'minigame';
  readonly scope: string;
  readonly payload: Record<string, unknown>;
  readonly suspiciousReasons?: readonly string[];
}

const DEBUG_STORAGE_KEY = 'smashparty:animationDebug';
const REPEATED_LOG_LIMIT = 1;
const ONE_SHOT_ALIASES = new Set([
  'punch',
  'knockdown',
  'standUp',
  'defeat',
  'victory',
  'diceStretch',
  'diceCatch',
  'drink',
  'emoteGG',
  'emoteOof',
  'emoteTaunt',
  'emoteThanks',
  'emoteCry',
  'emoteCheer',
]);
const LOCOMOTION_ALIASES = new Set([
  'idle',
  'walk',
  'idleTurnLeft',
  'idleTurnRight',
  'walkTurnLeft',
  'walkTurnRight',
]);
const BLOCKING_ONE_SHOT_ALIASES = new Set([
  'punch',
  'knockdown',
  'standUp',
  'defeat',
  'victory',
  'diceStretch',
  'diceCatch',
  'drink',
]);
const lastLogKeys = new Map<string, { key: string; count: number }>();
let overlay: CharacterAnimationDebugOverlay | null = null;

export function getCharacterAnimationDebugMode(): CharacterAnimationDebugMode {
  try {
    const value = globalThis.localStorage?.getItem(DEBUG_STORAGE_KEY);
    if (value === 'verbose') return 'verbose';
    if (value === '1') return 'standard';
  } catch {
    return 'off';
  }
  return 'off';
}

export function isCharacterAnimationDebugEnabled(): boolean {
  return getCharacterAnimationDebugMode() !== 'off';
}

export function logCharacterAnimationDebug(event: CharacterAnimationLogEvent): void {
  const mode = getCharacterAnimationDebugMode();
  if (mode === 'off') return;

  const suspiciousReasons = event.suspiciousReasons ?? [];
  const suspicious = suspiciousReasons.length > 0;
  const logKey = `${event.source}:${event.scope}:${stableDebugKey({
    ...event.payload,
    suspiciousReasons,
  })}`;
  if (!suspicious && mode !== 'verbose') {
    const previous = lastLogKeys.get(event.scope);
    if (previous?.key === logKey) {
      previous.count += 1;
      if (previous.count > REPEATED_LOG_LIMIT) return;
    } else {
      lastLogKeys.set(event.scope, { key: logKey, count: 1 });
    }
  }

  console.warn('[character-animation]', {
    source: event.source,
    scope: event.scope,
    suspiciousReasons,
    ...event.payload,
  });
}

export function suspiciousReasonsForMinigamePayload(
  payload: MinigameCharacterAnimationDebugPayload,
): string[] {
  const reasons: string[] = [];
  if (!payload.playedRequestedAlias) {
    reasons.push(`requested alias failed: ${payload.requestedAlias}`);
  }
  if (payload.fallbackReason?.startsWith('fallback alias failed') === true) {
    reasons.push(payload.fallbackReason);
  }
  const active = payload.rigState?.activeAfter;
  if (active !== null && active !== undefined) {
    if (LOCOMOTION_ALIASES.has(payload.selectedAlias) && active.alias !== null) {
      const activeAlias = active.alias;
      if (BLOCKING_ONE_SHOT_ALIASES.has(activeAlias)) {
        reasons.push(`locomotion selected but one-shot remains active: ${activeAlias}`);
      }
    }
    if (active.alias !== null && ONE_SHOT_ALIASES.has(active.alias) && !active.running) {
      reasons.push(`one-shot appears active after finishing: ${active.alias}`);
    }
  }
  return reasons;
}

export function logMinigameCharacterAnimation(
  payload: MinigameCharacterAnimationDebugPayload,
): void {
  const suspiciousReasons = suspiciousReasonsForMinigamePayload(payload);
  logCharacterAnimationDebug({
    source: 'minigame',
    scope: `${payload.minigameId}:${payload.playerId}`,
    suspiciousReasons,
    payload: {
      minigameId: payload.minigameId,
      playerId: payload.playerId,
      status: payload.status,
      speed: roundDebugNumber(payload.speed, 3),
      facingYaw: roundDebugNumber(payload.facingYaw, 3),
      deltaSeconds: roundDebugNumber(payload.deltaSeconds, 4),
      requestedAlias: payload.requestedAlias,
      selectedAlias: payload.selectedAlias,
      fallbackAlias: payload.fallbackAlias,
      playedRequestedAlias: payload.playedRequestedAlias,
      playedFallbackAlias: payload.playedFallbackAlias,
      playedIdleFallback: payload.playedIdleFallback,
      failureReason: payload.failureReason,
      fallbackReason: payload.fallbackReason,
      activeClip: payload.rigState?.activeAfter?.clipName ?? null,
      activeAlias: payload.rigState?.activeAfter?.alias ?? null,
      actionTime: payload.rigState?.activeAfter?.time ?? null,
      rigState: payload.rigState,
    },
  });
  if (payload.isLocalPlayer === true) {
    updateCharacterAnimationDebugOverlay(payload, suspiciousReasons);
  }
}

export function updateCharacterAnimationDebugOverlay(
  payload: MinigameCharacterAnimationDebugPayload,
  suspiciousReasons: readonly string[] = suspiciousReasonsForMinigamePayload(payload),
): void {
  if (!isCharacterAnimationDebugEnabled()) {
    overlay?.dispose();
    overlay = null;
    return;
  }
  if (typeof document === 'undefined') return;
  if (overlay === null) overlay = createCharacterAnimationDebugOverlay();
  overlay.update(payload, suspiciousReasons);
}

export function disposeCharacterAnimationDebugOverlay(): void {
  overlay?.dispose();
  overlay = null;
}

export function roundDebugNumber(value: number, places: number): number {
  return Number(value.toFixed(places));
}

function stableDebugKey(value: unknown): string {
  return JSON.stringify(value, (_key, item: unknown) => {
    if (typeof item === 'number') return Number(item.toFixed(4));
    return item;
  });
}

interface CharacterAnimationDebugOverlay {
  update(
    payload: MinigameCharacterAnimationDebugPayload,
    suspiciousReasons: readonly string[],
  ): void;
  dispose(): void;
}

function createCharacterAnimationDebugOverlay(): CharacterAnimationDebugOverlay {
  const element = document.createElement('div');
  element.className = 'smashparty-animation-debug-overlay';
  element.style.cssText = [
    'position: fixed',
    'left: 8px',
    'bottom: 8px',
    'z-index: 10000',
    'max-width: min(360px, calc(100vw - 16px))',
    'background: rgba(0,0,0,0.72)',
    'color: #fff',
    'font: 11px/1.35 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    'padding: 8px 10px',
    'border: 1px solid rgba(255,255,255,0.18)',
    'border-radius: 6px',
    'pointer-events: none',
    'white-space: pre-wrap',
  ].join('; ');
  document.body.append(element);

  return {
    update(payload, suspiciousReasons): void {
      const action = payload.rigState?.activeAfter;
      element.textContent = [
        `anim ${payload.minigameId}:${payload.playerId}`,
        `status ${payload.status}`,
        `requested ${payload.requestedAlias}`,
        `selected ${payload.selectedAlias}`,
        `active ${action?.alias ?? '-'} / ${action?.clipName ?? '-'}`,
        `time ${roundDebugNumber(action?.time ?? 0, 3)} running ${String(action?.running ?? false)}`,
        `failure ${payload.failureReason ?? '-'}`,
        `fallback ${payload.fallbackReason ?? '-'}`,
        suspiciousReasons.length > 0 ? `suspicious ${suspiciousReasons.join('; ')}` : '',
      ]
        .filter(Boolean)
        .join('\n');
    },
    dispose(): void {
      element.remove();
    },
  };
}
